import {
    AdminInfluencer,
    getAdminInfluencers,
    getAllInfluencerCosts,
    getInstagramUserImages,
    getTiktokUserImages,
    InfluencerCost,
    InstagramUserImage,
    TiktokUserImage,
} from '@round/api';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useState } from 'react';

type UseAdminInfluencersDataResult = {
    influencers: AdminInfluencer[];
    influencerCount: number | null;
    influencerCosts: InfluencerCost[];
    instagramUserImages: InstagramUserImage[];
    tiktokUserImages: TiktokUserImage[];

    isInitialized: boolean;
    isLoading: boolean;
    hasErrorLoadingData: boolean;
    hasNextPage: boolean;

    init: (requestInit?: RequestInit) => Promise<void>;
    loadNextPage: (requestInit?: RequestInit) => Promise<void>;
    reset: () => void;
    addInfluencer: (influencer: AdminInfluencer) => void;
    addCosts: (costs: InfluencerCost[]) => void;
    updateInfluencer: (influencer: Omit<AdminInfluencer, 'booking_count'>) => void;
    refreshInfluencerCosts: (influencerId: number) => Promise<void>;
};

type UseAdminInfluencersDataParams = Pick<
    Parameters<typeof getAdminInfluencers>[0],
    'page_size' | 'search' | 'manager_id' | 'roster_share_agency_id' | 'ordering'
>;

export default function useAdminInfluencersData({
    page_size,
    search,
    manager_id,
    roster_share_agency_id,
    ordering,
}: UseAdminInfluencersDataParams): UseAdminInfluencersDataResult {
    const [page, setPage] = useState(1);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasErrorLoadingData, setHasErrorLoadingData] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [influencers, setInfluencers] = useState<AdminInfluencer[]>([]);
    const [influencerCount, setInfluencerCount] = useState<number | null>(null);
    const [influencerCosts, setInfluencerCosts] = useState<InfluencerCost[]>([]);
    const [tiktokUserImages, setTiktokUserImages] = useState<TiktokUserImage[]>([]);
    const [instagramUserImages, setInstagramUserImages] = useState<InstagramUserImage[]>([]);

    const fetchInfluencerImages = useCallback(
        async (influencers: AdminInfluencer[]): Promise<[TiktokUserImage[], InstagramUserImage[]]> => {
            const tiktokUserIds: number[] = [];
            const instagramUserIds: number[] = [];
            influencers.forEach((inf) => {
                if (typeof inf.tiktok_user_id === 'number') {
                    tiktokUserIds.push(inf.tiktok_user_id);
                    return;
                }

                if (typeof inf.instagram_user_id === 'number') {
                    instagramUserIds.push(inf.instagram_user_id);
                    return;
                }
            });

            const fetchTiktokUserImages = async (ids: number[]) => {
                if (!ids.length) {
                    return [];
                }

                return getTiktokUserImages(ids);
            };

            const fetchInstagramImages = async (ids: number[]) => {
                if (!ids.length) {
                    return [];
                }

                return (await getInstagramUserImages(ids)).data;
            };

            const [tiktokImagesResult, instagramImagesResult] = await Promise.allSettled([
                fetchTiktokUserImages(tiktokUserIds),
                fetchInstagramImages(instagramUserIds),
            ]);

            const tiktokImages: TiktokUserImage[] =
                tiktokImagesResult.status === 'fulfilled' ? tiktokImagesResult.value : [];
            const instagramImages: InstagramUserImage[] =
                instagramImagesResult.status === 'fulfilled' ? instagramImagesResult.value : [];

            return [tiktokImages, instagramImages];
        },
        []
    );

    const fetchInfluencersData = useCallback(
        async (...params: Parameters<typeof getAdminInfluencers>) => {
            try {
                setIsLoading(true);
                setHasErrorLoadingData(false);
                const response = await getAdminInfluencers(...params);
                if (response.status !== 200) {
                    setHasErrorLoadingData(true);
                    return;
                }

                const influencers = response.data.results;
                setInfluencers((prev) => prev.concat(influencers));
                setInfluencerCount(response.data.count);
                setHasNextPage(Boolean(response.data.next));

                if (!influencers.length) {
                    return;
                }

                const costs = await getAllInfluencerCosts({ id: influencers.map((inf) => inf.id).join() });
                setInfluencerCosts((prev) => prev.concat(costs));
                const [tiktokUserImages, instagramUserImages] = await fetchInfluencerImages(influencers);
                setTiktokUserImages((prev) => prev.concat(tiktokUserImages));
                setInstagramUserImages((prev) => prev.concat(instagramUserImages));
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoadingData(true);
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        [fetchInfluencerImages]
    );

    const reset = useCallback(() => {
        setInfluencers([]);
        setInfluencerCount(null);
        setInfluencerCosts([]);
        setTiktokUserImages([]);
        setInstagramUserImages([]);

        setPage(1);
        setIsLoading(false);
        setIsInitialized(false);
        setHasNextPage(false);
        setHasErrorLoadingData(false);
    }, []);

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                await fetchInfluencersData(
                    { page, page_size, search, manager_id, roster_share_agency_id, ordering },
                    requestInit
                );
                setIsInitialized(true);
            } catch (e) {
                throw e;
            }
        },
        [fetchInfluencersData, page, page_size, search, manager_id, roster_share_agency_id, ordering]
    );

    const loadNextPage = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                await fetchInfluencersData(
                    { page: page + 1, page_size, search, manager_id, roster_share_agency_id, ordering },
                    requestInit
                );
                setPage((page) => page + 1);
            } catch (e) {
                throw e;
            }
        },
        [fetchInfluencersData, page, page_size, search, manager_id, roster_share_agency_id, ordering]
    );

    const debouncedLoadNextPage = useMemo(
        () => debounce(loadNextPage, 500, { leading: true, trailing: false }),
        [loadNextPage]
    );

    const addInfluencer = useCallback((influencer: AdminInfluencer) => {
        setInfluencers((prev) => [influencer, ...prev]);
    }, []);

    const addCosts = useCallback((costs: InfluencerCost[]) => {
        setInfluencerCosts((prev) => prev.concat(costs));
    }, []);

    const updateInfluencer = useCallback((influencer: Omit<AdminInfluencer, 'booking_count'>) => {
        setInfluencers((influencers) =>
            influencers.map((inf) =>
                inf.id === influencer.id ? { ...influencer, booking_count: inf.booking_count } : inf
            )
        );
    }, []);

    const refreshInfluencerCosts = useCallback(
        async (influencerId: number) => {
            const influencer = influencers.find((inf) => inf.id === influencerId);
            if (!influencer) {
                return;
            }

            try {
                const costs = await getAllInfluencerCosts({ id: influencerId.toString() });
                setInfluencerCosts((prev) => prev.filter((cost) => cost.influencer !== influencerId).concat(costs));
            } catch {
                throw new Error('Could not refresh influencer costs');
            }
        },
        [influencers]
    );

    return useMemo(
        () => ({
            influencers,
            influencerCount,
            influencerCosts,
            tiktokUserImages,
            instagramUserImages,

            hasNextPage,
            isLoading,
            hasErrorLoadingData,
            isInitialized,

            init,
            loadNextPage: debouncedLoadNextPage,
            reset,
            addInfluencer,
            addCosts,
            updateInfluencer,
            refreshInfluencerCosts,
        }),
        [
            addCosts,
            addInfluencer,
            hasErrorLoadingData,
            hasNextPage,
            influencerCosts,
            influencers,
            influencerCount,
            init,
            instagramUserImages,
            isInitialized,
            isLoading,
            debouncedLoadNextPage,
            refreshInfluencerCosts,
            reset,
            tiktokUserImages,
            updateInfluencer,
        ]
    );
}
